import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getBackendUrl } from '../utils/api';

// Modern SVG Icons
const HomeIcon = ({ active }) => (
  <svg className={`w-7 h-7 ${active ? 'text-blue-700' : 'text-gray-400'}`} fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const CalendarIcon = ({ active }) => (
  <svg className={`w-7 h-7 ${active ? 'text-blue-700' : 'text-gray-400'}`} fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const FingerPrintIcon = () => (
  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
  </svg>
);

const WalletIcon = ({ active }) => (
  <svg className={`w-7 h-7 ${active ? 'text-blue-700' : 'text-gray-400'}`} fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const MoreIcon = ({ active }) => (
  <svg className={`w-7 h-7 ${active ? 'text-blue-700' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 24 24">
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
);

// More Menu Icons
const menuIcons = {
  attendance: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  performance: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  tasks: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  ),
  bonus: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
    </svg>
  ),
  penalty: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  employees: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  branches: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  departments: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  ),
  requests: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
    </svg>
  ),
  salaries: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  reports: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  settings: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  security: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  ),
  language: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  logout: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  ),
  upgrade: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  ),
};

// Bottom Sheet Component
const MoreBottomSheet = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const isAdmin = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'general_manager';
  const [companyFeatures, setCompanyFeatures] = useState([]);
  const BACKEND_URL = getBackendUrl();
  
  // Check if feature is available
  const hasFeature = (feature) => companyFeatures.includes(feature);
  
  // Fetch company features
  useEffect(() => {
    if (!isOpen) return;
    const token = localStorage.getItem('session_token');
    if (!token) return;
    
    axios.get(`${BACKEND_URL}/api/companies/me`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setCompanyFeatures(res.data?.features || []);
    }).catch(() => {});
  }, [isOpen, BACKEND_URL]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
    window.location.reload();
  };

  const handleLogout = () => {
    logout();
    onClose();
    navigate('/login');
  };

  const handleNavigate = (path) => {
    onClose();
    navigate(path);
  };

  // Base sections (always visible)
  const sections = [
    {
      title: isRTL ? 'الأداء' : 'Performance',
      items: [
        { icon: menuIcons.attendance, label: isRTL ? 'سجل الحضور' : 'Attendance History', path: '/attendance-history', feature: null },
        ...(hasFeature('kpi') ? [{ icon: menuIcons.performance, label: isRTL ? 'تقييم الأداء' : 'Performance', path: '/performance', feature: 'kpi' }] : []),
      ]
    },
  ];

  // Admin sections - filtered by features
  const adminSections = [
    {
      title: isRTL ? 'إدارة النظام' : 'System Management',
      items: [
        { icon: menuIcons.employees, label: isRTL ? 'الموظفين' : 'Employees', path: '/employees' },
        { icon: menuIcons.branches, label: isRTL ? 'الفروع' : 'Branches', path: '/branches' },
        { icon: menuIcons.departments, label: isRTL ? 'الأقسام' : 'Departments', path: '/departments' },
        { icon: menuIcons.requests, label: isRTL ? 'طلبات الانضمام' : 'Join Requests', path: '/join-requests' },
        ...(hasFeature('salaries') ? [{ icon: menuIcons.salaries, label: isRTL ? 'إدارة الرواتب' : 'Salaries Management', path: '/salaries' }] : []),
        ...(hasFeature('reports') ? [{ icon: menuIcons.reports, label: isRTL ? 'التقارير' : 'Reports', path: '/reports' }] : []),
      ]
    },
  ];

  const settingsSection = {
    title: isRTL ? 'الإعدادات' : 'Settings',
    items: [
      { 
        icon: menuIcons.upgrade, 
        label: isRTL ? 'ترقية الخطة' : 'Upgrade Plan', 
        path: '/subscription',
        highlight: true
      },
      { 
        icon: menuIcons.language, 
        label: isRTL ? 'اللغة: العربية' : 'Language: English', 
        action: toggleLanguage,
        subtitle: isRTL ? 'English' : 'العربية'
      },
    ]
  };

  const allSections = isAdmin ? [...sections, ...adminSections, settingsSection] : [...sections, settingsSection];

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Bottom Sheet */}
      <div 
        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 transform transition-transform duration-300 ease-out"
        style={{ height: '78vh' }}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Handle Bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto px-4 pb-8" style={{ height: 'calc(78vh - 40px)' }}>
          
          {allSections.map((section, sIdx) => (
            <div key={sIdx} className="mb-4">
              <h3 className="text-gray-400 font-medium text-xs uppercase tracking-wide mb-2 px-1">{section.title}</h3>
              <div className="bg-gray-50 rounded-xl overflow-hidden">
                {section.items.map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => item.action ? item.action() : handleNavigate(item.path)}
                    className={`w-full px-4 py-3 flex items-center gap-3 transition-colors border-b border-gray-100 last:border-b-0 ${
                      item.highlight 
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100' 
                        : 'hover:bg-gray-100 active:bg-gray-200'
                    }`}
                  >
                    <span className={item.highlight ? 'text-blue-600' : 'text-gray-500'}>{item.icon}</span>
                    <div className="flex-1 text-start">
                      <p className={`text-sm ${item.highlight ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>{item.label}</p>
                      {item.subtitle && <p className="text-gray-400 text-xs">{item.subtitle}</p>}
                    </div>
                    <svg className={`w-4 h-4 ${item.highlight ? 'text-blue-400' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Logout Button - Compact */}
          <div className="flex justify-center mt-6">
            <button
              onClick={handleLogout}
              className="px-8 py-2 bg-red-50 text-red-500 rounded-full font-medium flex items-center gap-2 hover:bg-red-100 active:bg-red-200 transition-colors text-sm"
            >
              {menuIcons.logout}
              {isRTL ? 'تسجيل الخروج' : 'Logout'}
            </button>
          </div>

          {/* App Version */}
          <p className="text-center text-gray-300 text-xs py-4">
            HELCOO HR v2.0
          </p>
        </div>
      </div>
    </>
  );
};

// Bottom Navigation Component
export const BottomNavigation = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isRTL = i18n.language === 'ar';
  const [showMoreSheet, setShowMoreSheet] = useState(false);
  const [companyFeatures, setCompanyFeatures] = useState([]);
  const BACKEND_URL = getBackendUrl();
  
  // Fetch company features
  useEffect(() => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    
    axios.get(`${BACKEND_URL}/api/companies/me`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setCompanyFeatures(res.data?.features || []);
    }).catch(() => {});
  }, [BACKEND_URL]);
  
  // Check if feature is available
  const hasFeature = (feature) => companyFeatures.includes(feature);

  // Navigation items - filtered by features
  const allNavItems = [
    { icon: HomeIcon, label: isRTL ? 'الرئيسية' : 'Home', path: '/dashboard', feature: null },
    { icon: CalendarIcon, label: isRTL ? 'الإجازات' : 'Leaves', path: '/leaves', feature: null },
    { icon: FingerPrintIcon, label: isRTL ? 'الحضور' : 'Attendance', path: '/attendance', main: true, feature: null },
    { icon: WalletIcon, label: isRTL ? 'راتبي' : 'Salary', path: '/my-salary', feature: 'salaries' },
    { icon: MoreIcon, label: isRTL ? 'المزيد' : 'More', action: () => setShowMoreSheet(true), feature: null },
  ];
  
  // Filter nav items based on features
  const navItems = allNavItems.filter(item => 
    item.feature === null || hasFeature(item.feature)
  );

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-2 py-3 z-40 safe-area-pb shadow-lg shadow-gray-200/50">
        <div className="flex items-center justify-around max-w-lg mx-auto">
          {navItems.map((item, idx) => {
            const IconComponent = item.icon;
            const active = item.path ? isActive(item.path) : false;
            return (
              <button
                key={idx}
                onClick={() => item.action ? item.action() : navigate(item.path)}
                className={`flex flex-col items-center transition-all duration-200 ${item.main ? '-mt-6' : 'py-1'}`}
              >
                {item.main ? (
                  <div className="w-16 h-16 bg-gradient-to-br from-[#1E40AF] to-[#3B82F6] rounded-full flex items-center justify-center shadow-lg shadow-blue-900/40 border-4 border-white active:scale-95 transition-transform">
                    <IconComponent />
                  </div>
                ) : (
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${active ? 'bg-blue-50' : ''}`}>
                    <IconComponent active={active} />
                  </div>
                )}
                <span className={`text-xs mt-1 ${active ? 'text-blue-700 font-semibold' : 'text-gray-400'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* More Bottom Sheet */}
      <MoreBottomSheet isOpen={showMoreSheet} onClose={() => setShowMoreSheet(false)} />
    </>
  );
};

export default BottomNavigation;
